import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  Fab, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  getDevices, getActiveSessions, getActivityStatus, triggerActivityAction,
  getActivityActionStatus, getActivityScreenshots, getActivityScreenshotUrl,
  getActivityBrowserHistory, getActivityBrowserHistoryEntries, getActivityPolicy,
  getEffectiveFeatureConfig,
} from '../api/client';
import { useSSE } from '../hooks/useSSE';

// Job-lifecycle status ONLY — matches db.workforce_models.ActivityJobStatus
// on the server exactly. "connection_lost"/"device_offline" are
// deliberately absent: those are transport facts, never a job status (see
// services/device_commands.py's module docstring on the server). A
// WebSocket disconnecting does not mean the job failed — capture runs on
// the edge's own thread and SyncWorker uploads independently, on its own
// schedule, regardless of the control WebSocket, including across an edge
// process restart.
const STATUS_LABEL = {
  requested: 'Requested', capturing: 'Capturing', uploading: 'Uploading',
  completed: 'Completed', failed: 'Failed',
  // Server-decided (the reaper, main.py) — no terminal status from the
  // edge arrived within the job's deadline. Replaces the old client-side
  // 45-second poll budget; this is a real backend outcome now, not a
  // frontend guess.
  expired: 'Timed Out — Try Again',
  // These two are POST-response-only outcomes (see triggerAction below):
  // dispatch itself never reached the edge, so no ActivityJob was ever
  // created — there is nothing to poll or subscribe to. Kept distinct
  // from job statuses on purpose (server/api/activity.py's
  // _connection_down_response docstring): the device's heartbeat can be
  // fully healthy while only its control WebSocket is momentarily down.
  device_offline: 'Device Offline',
  stream_disconnected: 'Device Online — Control Channel Reconnecting',
  capture_already_in_progress: 'Already In Progress',
};

// The single source of truth for "is this job over" on the client —
// mirrors db.workforce_models.ACTIVITY_JOB_TERMINAL_STATUSES exactly.
// Both the SSE handler and the resilience-poll fallback below use this
// same set, so they can never disagree about when to stop.
const JOB_TERMINAL_STATUSES = new Set(['completed', 'failed', 'expired']);

// Resilience-poll fallback cadence — NOT a budget. This is not a revival
// of the old 30-attempt/45s ceiling: it never stops on attempt count, only
// ever on a real terminal status (JOB_TERMINAL_STATUSES) or the component
// unmounting/the SSE connection coming back up. It exists only to cover
// the gap while the SSE connection itself is down (useSSE's own
// exponential backoff can take up to 30s to reconnect) — primary delivery
// is push (see the activity_status SSE handler below), this is strictly a
// backup so a request's real outcome is never missed even if SSE never
// reconnects for this browser tab.
const FALLBACK_POLL_INTERVAL_MS = 5000;

// How long the Capture button's success ("Captured") / failure state
// stays visible before it reverts to the idle state — purely a display
// timer, independent of the job's actual (already-terminal) backend
// status; the request itself is already fully resolved by this point.
const ACTION_FLASH_DISPLAY_MS = 2500;

// Primary action button for manual screenshot capture. Purely
// presentational — driven entirely by props already computed from
// existing state (`pending`); no new API calls, no backend-facing logic.
// Four visual states: idle / processing / success / failure, per the
// Work Activity UX spec. The History trigger deliberately stays a plain
// secondary IconButton (see the render below) — only Screenshot gets this
// treatment.
function ScreenshotActionButton({ phase, disabledReason, onClick, disabled }) {
  const label = {
    idle: 'Capture', processing: 'Capturing…', success: 'Captured', failure: 'Failed',
  }[phase];

  const tooltipTitle = disabledReason || 'Capture Screenshot';

  return (
    <Tooltip title={tooltipTitle} arrow>
      {/* Tooltip needs a non-disabled child to attach listeners to when
          the Fab itself is disabled — the wrapping span is the standard
          MUI pattern for this (same one already used elsewhere on this
          page for the other icon buttons). */}
      <span>
        <Fab
          variant="extended"
          size="medium"
          aria-label={phase === 'idle' ? 'Capture Screenshot' : label}
          disabled={disabled || phase === 'processing'}
          onClick={onClick}
          sx={{
            textTransform: 'none',
            minWidth: 132,
            justifyContent: 'flex-start',
            color: '#fff',
            bgcolor:
              phase === 'success' ? 'success.main'
              : phase === 'failure' ? 'error.main'
              : 'primary.main',
            boxShadow: 2,
            transition: 'transform 150ms ease, box-shadow 150ms ease, background-color 150ms ease',
            cursor: disabled || phase === 'processing' ? 'default' : 'pointer',
            '&:hover': {
              bgcolor:
                phase === 'success' ? 'success.dark'
                : phase === 'failure' ? 'error.dark'
                : 'primary.dark',
              boxShadow: 6,
              transform: (disabled || phase === 'processing') ? 'none' : 'scale(1.05)',
            },
            '&.Mui-disabled': {
              color: '#fff',
              bgcolor:
                phase === 'success' ? 'success.main'
                : phase === 'failure' ? 'error.main'
                : phase === 'processing' ? 'primary.main'
                : 'action.disabledBackground',
              opacity: phase === 'processing' ? 0.85 : 1,
            },
          }}
        >
          {phase === 'processing' && (
            <CircularProgress size={18} thickness={5} sx={{ color: '#fff', mr: 1 }} />
          )}
          {phase === 'idle' && <CameraAltIcon sx={{ mr: 1 }} fontSize="small" />}
          {phase === 'success' && <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" />}
          {phase === 'failure' && <WarningAmberIcon sx={{ mr: 1 }} fontSize="small" />}
          {label}
        </Fab>
      </span>
    </Tooltip>
  );
}

function timeAgo(iso) {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/** Supervisor: Work Activity — one row per assigned device, manual capture
 * actions, and history viewers. Manual-action status is delivered via the
 * shared SSE broadcaster (push), with a connectivity-gated slow poll as a
 * fallback only — see JOB_TERMINAL_STATUSES/FALLBACK_POLL_INTERVAL_MS. */
export default function WorkActivityPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState({});     // device_id -> {status, requestId, terminal, connectivityHint}
  // device_id -> request_id, only while that job is non-terminal — the
  // guard both the SSE handler and the fallback poll use to ignore a
  // stale event/response for a request the user has since moved past
  // (e.g. a slow late "completed" for a request_id that was superseded
  // by a new manual trigger on the same device).
  const trackedRequestsRef = useRef({});
  // device_id -> setTimeout id for the success/failure flash's auto-revert
  // — display-only bookkeeping, cleared/replaced whenever a device's
  // pending state changes again before the timer fires.
  const flashTimersRef = useRef({});
  useEffect(() => () => {
    Object.values(flashTimersRef.current).forEach(clearTimeout);
  }, []);
  const [screenshotViewer, setScreenshotViewer] = useState(null); // {url}
  const [historyViewer, setHistoryViewer] = useState(null);       // {entries}
  // Effective (license AND vendor-admin toggle) per-feature enablement —
  // a vendor can license/enable only one of the two, so each button/column
  // is gated independently, not just by "is this page reachable at all"
  // (AppLayout's nav gate already covers that coarser case).
  const [policy, setPolicy] = useState(null);
  useEffect(() => { getActivityPolicy().then(setPolicy).catch(() => {}); }, []);
  const screenshotOn = Boolean(policy?.screenshot_enabled);
  const historyOn = Boolean(policy?.browser_history_enabled);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [devices, sessions] = await Promise.all([getDevices(), getActiveSessions()]);
      const operatorByDevice = Object.fromEntries(
        sessions.filter(s => s.operator).map(s => [s.device_id, s.operator.name])
      );
      const [statuses, effectiveConfigs] = await Promise.all([
        Promise.all(devices.map(d => getActivityStatus(d.id).catch(() => null))),
        Promise.all(devices.map(d => getEffectiveFeatureConfig(d.id).catch(() => null))),
      ]);
      setRows(devices.map((d, i) => ({
        device: d,
        operatorName: operatorByDevice[d.id] || '—',
        status: statuses[i],
        effective: effectiveConfigs[i],
      })));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  // Display-only: once a device's pending entry goes terminal, keep the
  // success/failure flash on screen for ACTION_FLASH_DISPLAY_MS, then
  // clear it so the button reverts to idle. Guarded by requestId so a
  // fresh trigger started during the flash window is never clobbered by
  // a stale timer clearing it out from under the new request.
  const scheduleFlashRevert = useCallback((deviceId, requestId) => {
    clearTimeout(flashTimersRef.current[deviceId]);
    flashTimersRef.current[deviceId] = setTimeout(() => {
      setPending(prev => (
        prev[deviceId]?.requestId === requestId
          ? { ...prev, [deviceId]: undefined }
          : prev
      ));
    }, ACTION_FLASH_DISPLAY_MS);
  }, []);

  // Applies one job-status update for a device, but only if it's still
  // the request we're actually tracking — guards against a stale/late
  // event for a request_id the user has since moved past (superseded by
  // a new trigger on the same device). Stops tracking once terminal;
  // JOB_TERMINAL_STATUSES is the one place "is this over" is decided,
  // shared by both delivery paths below so they can't disagree.
  const applyJobUpdate = useCallback((deviceId, requestId, entry) => {
    if (trackedRequestsRef.current[deviceId] !== requestId) return;
    const terminal = JOB_TERMINAL_STATUSES.has(entry.status);
    setPending(prev => ({
      ...prev,
      [deviceId]: {
        ...prev[deviceId],
        status: entry.status, requestId, terminal,
        connectivityHint: entry.connectivity_hint || null,
      },
    }));
    if (terminal) {
      delete trackedRequestsRef.current[deviceId];
      load();
      scheduleFlashRevert(deviceId, requestId);
    }
  }, [load, scheduleFlashRevert]);

  // Primary delivery: push, via the existing SSE broadcaster (already
  // published on every job-status change server-side — see
  // api/activity.py's broadcaster.broadcast("activity_status", ...) and
  // main.py's reaper task). No polling loop, no budget: this fires once
  // per real state change, for as long as the tab is mounted.
  const handleActivityStatus = useCallback((payload) => {
    if (!payload?.device_id || !payload?.request_id) return;
    applyJobUpdate(payload.device_id, payload.request_id, payload);
  }, [applyJobUpdate]);

  const { connected: sseConnected } = useSSE(null, null, null, handleActivityStatus);

  // Resilience-poll fallback — see FALLBACK_POLL_INTERVAL_MS's comment.
  // Only runs while SSE is down, and even then only for requests still
  // being tracked; never a fixed attempt count, only ever stops on a
  // real terminal status or this effect's own cleanup (unmount).
  useEffect(() => {
    if (sseConnected) return;
    const t = setInterval(() => {
      for (const [deviceId, requestId] of Object.entries(trackedRequestsRef.current)) {
        getActivityActionStatus(requestId)
          .then(entry => applyJobUpdate(deviceId, requestId, entry))
          .catch(() => { /* transient — next tick retries, no budget to exhaust */ });
      }
    }, FALLBACK_POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [sseConnected, applyJobUpdate]);

  const triggerAction = async (deviceId, action) => {
    setPending(prev => ({ ...prev, [deviceId]: { status: 'requested', action } }));
    try {
      const resp = await triggerActivityAction(deviceId, action);
      if (resp.status === 'device_offline' || resp.status === 'stream_disconnected') {
        // No ActivityJob was ever created — dispatch itself never
        // reached the edge, so there is nothing to track. Terminal from
        // the UI's perspective: the user must click again to retry.
        setPending(prev => ({ ...prev, [deviceId]: { status: resp.status, reason: resp.detail, terminal: true, action } }));
        scheduleFlashRevert(deviceId, undefined);
        return;
      }
      if (resp.status === 'capture_already_in_progress') {
        setPending(prev => ({ ...prev, [deviceId]: { status: 'capture_already_in_progress', terminal: true, action } }));
        scheduleFlashRevert(deviceId, undefined);
        return;
      }
      trackedRequestsRef.current[deviceId] = resp.request_id;
      setPending(prev => ({ ...prev, [deviceId]: { status: 'requested', requestId: resp.request_id, terminal: false, action } }));
    } catch (e) {
      const reason = e.response?.data?.detail;
      setPending(prev => ({ ...prev, [deviceId]: { status: 'failed', reason, terminal: true, action } }));
      scheduleFlashRevert(deviceId, undefined);
      if (reason) setError(reason);
    }
  };

  const openLatestScreenshot = async (deviceId) => {
    const list = await getActivityScreenshots({ device_id: deviceId, limit: 1 });
    if (list.length === 0) return;
    setScreenshotViewer({ url: getActivityScreenshotUrl(list[0].id) });
  };

  const openLatestHistory = async (deviceId) => {
    const list = await getActivityBrowserHistory({ device_id: deviceId, limit: 1 });
    if (list.length === 0) return;
    const entries = await getActivityBrowserHistoryEntries(list[0].id);
    setHistoryViewer({ entries });
  };

  if (loading && rows.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Work Activity</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Operator</TableCell>
            <TableCell>Device</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Last Screenshot</TableCell>
            <TableCell>Last Browser Sync</TableCell>
            <TableCell>Actions</TableCell>
            <TableCell>History</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ device, operatorName, status, effective }) => {
            const p = pending[device.id];
            // Scoped per action kind — a screenshot in flight must not
            // disable the (independent) history button and vice versa,
            // matching the server's per-(device,kind) lock. Same
            // `terminal` flag applyJobUpdate/triggerAction already set on
            // `p` — one definition of "is this action still in flight",
            // not a second hardcoded status list that could drift from it.
            const screenshotP = p?.action === 'capture_screenshot' ? p : null;
            const historyP = p?.action === 'fetch_browser_history' ? p : null;
            const historyBusy = historyP && !historyP.terminal;
            const screenshotAssigned = effective?.screenshot?.assigned !== false; // fail open until loaded
            const historyAssigned = effective?.browser_history?.assigned !== false;
            const screenshotPhase = !screenshotP ? 'idle'
              : !screenshotP.terminal ? 'processing'
              : screenshotP.status === 'completed' ? 'success'
              : 'failure';
            const screenshotFailureReason = screenshotPhase === 'failure'
              ? (screenshotP.reason || STATUS_LABEL[screenshotP.status] || screenshotP.status)
              : null;
            return (
              <TableRow key={device.id} hover>
                <TableCell>{operatorName}</TableCell>
                <TableCell>{device.hostname || device.id.slice(0, 8)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={status?.online ? 'Online' : 'Offline'}
                    color={status?.online ? 'success' : 'default'}
                  />
                  {p && (
                    <Chip size="small" sx={{ ml: 1 }} label={STATUS_LABEL[p.status] || p.status} />
                  )}
                  {/* Device connectivity is rendered separately from the
                      job's own status — never folded into one fake
                      combined state (see STATUS_LABEL's module comment).
                      Only shown while a job is still in flight and the
                      control channel was last seen disconnected; purely
                      informational, never gates the button or the job's
                      own status. */}
                  {p && !p.terminal && p.connectivityHint?.last_known === 'disconnected' && (
                    <Chip size="small" sx={{ ml: 1 }} variant="outlined" color="warning"
                      label="Device reconnecting…" />
                  )}
                </TableCell>
                <TableCell>{timeAgo(status?.last_screenshot_at)}</TableCell>
                <TableCell>{timeAgo(status?.last_browser_sync_at)}</TableCell>
                <TableCell>
                  {screenshotOn && (
                    <ScreenshotActionButton
                      phase={screenshotPhase}
                      disabled={!screenshotAssigned}
                      disabledReason={
                        !screenshotAssigned
                          ? 'This device is not assigned for Screenshot Monitoring. Assign this feature from Alarm Assignment first.'
                          : screenshotFailureReason
                      }
                      onClick={() => triggerAction(device.id, 'capture_screenshot')}
                    />
                  )}
                  {historyOn && (
                    // Kept visually secondary (outlined, neutral color) on
                    // purpose — only the Screenshot action gets the
                    // primary treatment (see ScreenshotActionButton above).
                    <Tooltip title={historyAssigned ? 'Fetch Browser History' : 'This device is not assigned for Browser History Monitoring. Assign this feature from Alarm Assignment first.'}>
                      <span>
                        <IconButton
                          size="small"
                          sx={{ ml: 1, border: '1px solid', borderColor: 'divider' }}
                          disabled={historyBusy || !historyAssigned}
                          onClick={() => triggerAction(device.id, 'fetch_browser_history')}
                        >
                          {historyBusy
                            ? <CircularProgress size={16} thickness={5} />
                            : <HistoryIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {!screenshotOn && !historyOn && '—'}
                </TableCell>
                <TableCell>
                  {screenshotOn && (
                    <Tooltip title="View latest screenshot">
                      <span>
                        <IconButton size="small" onClick={() => openLatestScreenshot(device.id)}>
                          <CameraAltIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {historyOn && (
                    <Tooltip title="View today's browser history">
                      <span>
                        <IconButton size="small" onClick={() => openLatestHistory(device.id)}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {!screenshotOn && !historyOn && '—'}
                </TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={7} align="center">No devices assigned to you.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={!!screenshotViewer} onClose={() => setScreenshotViewer(null)} maxWidth="lg">
        <DialogTitle>
          Screenshot
          <IconButton onClick={() => setScreenshotViewer(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {screenshotViewer && <img src={screenshotViewer.url} alt="Screenshot" style={{ maxWidth: '100%' }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyViewer} onClose={() => setHistoryViewer(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Browser History (Today)
          <IconButton onClick={() => setHistoryViewer(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Browser</TableCell><TableCell>Title</TableCell>
                <TableCell>URL</TableCell><TableCell>Visited</TableCell><TableCell>Visits</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(historyViewer?.entries || []).map((e, i) => (
                <TableRow key={i}>
                  <TableCell>{e.browser}</TableCell>
                  <TableCell>{e.title || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.url}</TableCell>
                  <TableCell>{new Date(e.visited_at).toLocaleTimeString()}</TableCell>
                  <TableCell>{e.visit_count ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
